import { InjectionToken, Signal } from '@angular/core';

/**
 * One plain-language glossary entry, mirroring the server's `GET /api/glossary` shape
 * (Cohelion.Mcp.Server GlossaryEndpoints / GlossaryCatalog). Drives the inline term tooltips
 * and the "Terms in this answer" panel in assistant messages.
 */
export interface CsGlossaryTerm {
	/** Anchor-safe id, e.g. "mape". */
	slug: string;
	/** Display term, e.g. "MAPE". */
	term: string;
	/** Synonyms/spellings that should also trigger the tooltip. */
	aliases: string[];
	/** One-sentence plain definition (tooltip + panel). */
	short: string;
	/** Full markdown body (reserved for a future expandable definition). */
	long: string;
}

/**
 * Reactive glossary source provided by the host app. The shared chat components inject this
 * (optionally) to decorate assistant messages; when absent, messages render unchanged.
 */
export interface CsGlossarySource {
	/** Glossary terms, empty until loaded. Read inside computeds so the UI updates on load. */
	terms: Signal<CsGlossaryTerm[]>;
}

export const CS_CHAT_GLOSSARY = new InjectionToken<CsGlossarySource>('CS_CHAT_GLOSSARY');

interface Matcher {
	/** Global, case-insensitive alternation of every term + alias (longest first). */
	regex: RegExp;
	/** Lowercased phrase → its owning term. */
	lookup: Map<string, CsGlossaryTerm>;
}

const matcherCache = new WeakMap<CsGlossaryTerm[], Matcher | null>();

/**
 * Builds (and memoises per term-array reference) a single regex that matches any glossary term or
 * alias on a word boundary. Phrases are sorted longest-first so "coefficient of variation" wins
 * over a bare "variation". Returns null when there are no usable phrases.
 */
function buildMatcher(terms: CsGlossaryTerm[]): Matcher | null {
	if (matcherCache.has(terms)) { return matcherCache.get(terms) ?? null; }

	const lookup = new Map<string, CsGlossaryTerm>();
	for (const t of terms) {
		for (const phrase of [t.term, ...(t.aliases ?? [])]) {
			const key = phrase.trim().toLowerCase();
			if (key.length >= 2 && !lookup.has(key)) { lookup.set(key, t); }
		}
	}

	if (lookup.size === 0) { matcherCache.set(terms, null); return null; }

	const phrases = [...lookup.keys()].sort((a, b) => b.length - a.length).map(escapeRegExp);
	// \b around the alternation: word-boundary match so "CV" doesn't fire inside "CVS".
	const matcher: Matcher = { regex: new RegExp(`\\b(?:${phrases.join('|')})\\b`, 'gi'), lookup };
	matcherCache.set(terms, matcher);
	return matcher;
}

/** Tags whose text must never be decorated (code, links, headings, already-decorated spans). */
const SKIP_TAGS = new Set(['CODE', 'PRE', 'A', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'SCRIPT', 'STYLE']);

/**
 * Wraps the first occurrence of each glossary term in the rendered message HTML with a
 * dotted-underline span carrying a native `title` tooltip. Only the FIRST mention of each term is
 * decorated (per message) to avoid a sea of underlines. Returns the HTML unchanged when there is
 * no glossary, nothing matches, or DOM parsing is unavailable.
 */
export function decorateGlossaryHtml(html: string, terms: CsGlossaryTerm[]): string {
	const matcher = buildMatcher(terms);
	if (!matcher || typeof DOMParser === 'undefined') { return html; }

	const doc  = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
	const root = doc.body.firstElementChild;
	if (!root) { return html; }

	const seen = new Set<string>();
	decorateNode(root, doc, matcher, seen);
	return seen.size === 0 ? html : root.innerHTML;
}

function decorateNode(node: Element, doc: Document, matcher: Matcher, seen: Set<string>): void {
	// Snapshot childNodes — we mutate the tree while iterating.
	for (const child of Array.from(node.childNodes)) {
		if (child.nodeType === Node.TEXT_NODE) {
			decorateTextNode(child as Text, doc, matcher, seen);
		} else if (child.nodeType === Node.ELEMENT_NODE) {
			const el = child as Element;
			if (!SKIP_TAGS.has(el.tagName) && !el.classList.contains('cs-glossary-term')) {
				decorateNode(el, doc, matcher, seen);
			}
		}
	}
}

function decorateTextNode(text: Text, doc: Document, matcher: Matcher, seen: Set<string>): void {
	const value = text.nodeValue ?? '';
	matcher.regex.lastIndex = 0;
	if (!matcher.regex.test(value)) { return; }

	matcher.regex.lastIndex = 0;
	const frag = doc.createDocumentFragment();
	let last = 0;
	let m: RegExpExecArray | null;
	let changed = false;

	while ((m = matcher.regex.exec(value)) !== null) {
		const term = matcher.lookup.get(m[0].toLowerCase());
		if (!term || seen.has(term.slug)) { continue; }   // first mention only
		seen.add(term.slug);
		changed = true;

		if (m.index > last) { frag.appendChild(doc.createTextNode(value.slice(last, m.index))); }

		// Only `class` and `title` survive Angular's [innerHTML] sanitizer; richer attributes
		// (data-*, role, tabindex, aria-label) would be stripped with a console warning. The native
		// `title` tooltip is announced by screen readers and shows on hover — enough for the inline
		// hint; the "Terms in this answer" panel carries the fuller, fully-accessible list.
		const span = doc.createElement('span');
		span.className = 'cs-glossary-term';
		span.setAttribute('title', `${term.term} — ${term.short}`);
		span.textContent = m[0];
		frag.appendChild(span);
		last = m.index + m[0].length;
	}

	if (!changed) { return; }
	if (last < value.length) { frag.appendChild(doc.createTextNode(value.slice(last))); }
	text.parentNode?.replaceChild(frag, text);
}

/**
 * Returns the glossary terms that appear in `text` (plain text or HTML), in first-appearance order,
 * each at most once — the data behind the "Terms in this answer" panel.
 */
export function matchGlossaryTerms(text: string, terms: CsGlossaryTerm[]): CsGlossaryTerm[] {
	const matcher = buildMatcher(terms);
	if (!matcher || !text) { return []; }

	const found = new Map<string, { term: CsGlossaryTerm; at: number }>();
	matcher.regex.lastIndex = 0;
	let m: RegExpExecArray | null;
	while ((m = matcher.regex.exec(text)) !== null) {
		const term = matcher.lookup.get(m[0].toLowerCase());
		if (term && !found.has(term.slug)) { found.set(term.slug, { term, at: m.index }); }
	}
	return [...found.values()].sort((a, b) => a.at - b.at).map(x => x.term);
}

function escapeRegExp(s: string): string {
	return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
